<!--
  - Copyright (c) 2021 brightlamp
  -->

<template>
    <div>
        <v-dialog v-model="dialogLocal" persistent scrollable max-width="540px">
            <v-card>
                <v-card-title>
                    <span class="headline" align="center" style="color:#FFFD00;background-color:#363636;width:100%;display:block;">Patient Test Request</span>
                </v-card-title>
                <v-card-actions>
                    <v-btn color="blue darken-1" text @click="closeDialog">Close</v-btn>
                    <v-spacer/>
                    <v-btn color="blue darken-1" text @click="deleteTestRequest">Delete</v-btn>
                    <v-btn color="blue darken-1" text @click="checkBeforeSend">Update</v-btn>
                </v-card-actions>
                <v-progress-linear v-if="waiting" :indeterminate="true"/>
                <v-card-text id="patientTestRequestId">
                    <v-row>
                        <v-col :cols="12">
                            <v-checkbox
                                type="checkbox"
                                true-value="true"
                                label="Create For All Group Members"
                                v-model="localCreateForAllGroupMembers"
                                color="primary"
                                persistent-hint
                                hint="Check to create an identical test for all patients with the same group ID."/>
                        </v-col>
                    </v-row>
                    <ValidationObserver ref="observer" v-slot="{ invalid }">
                        <v-form>
                            <v-row>
                                <v-col :cols="8">
                                    <v-menu
                                        v-model="scheduledDateMenu"
                                        :close-on-content-click="false"
                                        transition="scale-transition"
                                        offset-y
                                        min-width="240px">
                                        <template v-slot:activator="{ on }">
                                            <v-text-field
                                                v-model="scheduledDateFormatted"
                                                label="Stop Date"
                                                readonly
                                                prepend-icon="mdi-calendar"
                                                v-on="on">
                                            </v-text-field>
                                        </template>
                                        <v-date-picker color="blue" v-model="scheduledDate" no-title @input="scheduledDateMenu = false"/>
                                    </v-menu>
                                </v-col>
                                <v-col :cols="4">
                                    <v-text-field
                                        v-model="scheduledTime"
                                        label="Stop Time"
                                        type="time">
                                    </v-text-field>
                                </v-col>
                            </v-row>
                            <v-row dense>
                                <v-col :cols="12">
                                    <ValidationProvider
                                        mode="aggressive"
                                        rules="requiredEyeChoice"
                                        :debounce="300"
                                        v-slot="{ errors }">
                                        <v-select
                                            v-model="eyeChoice"
                                            :items="eyeChoices"
                                            label="Eye Choice *"
                                            persistent-hint
                                            hint="Select which eye or both to test."
                                            :error-messages=errors[0]>
                                        </v-select>
                                    </ValidationProvider>
                                </v-col>
                                <v-col :cols="12">
                                    <v-checkbox
                                        type="checkbox"
                                        true-value="true"
                                        label="Baseline Test"
                                        v-model="baseline"
                                        color="primary"
                                        persistent-hint
                                        hint="Baseline or normal test."/>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col :cols="12">
                                    <ValidationProvider
                                        mode="aggressive"
                                        rules="requiredTestProtocol"
                                        :debounce="300"
                                        v-slot="{ errors }">
                                        <v-text-field
                                            v-model="testTag"
                                            label="Test Protocol Tag *"
                                            :readonly=true
                                            persistent-hint
                                            hint="Select testing parameters."
                                            @click="testProtocolListDialog = true"
                                            :error-messages=errors[0]>
                                            <template v-slot:prepend>
                                                <v-tooltip bottom color="black">
                                                    <template v-slot:activator="{ on }">
                                                        <v-icon v-on="on">
                                                            mdi-help-circle-outline
                                                        </v-icon>
                                                    </template>
                                                    A test protocol is a pre-defined set of recording settings.
                                                </v-tooltip>
                                            </template>
                                        </v-text-field>
                                    </ValidationProvider>
                                </v-col>
                                <v-col :cols="12">
                                    <v-textarea
                                        v-model="emailSubject"
                                        auto-grow
                                        label="Email Subject"
                                        rows="1">
                                    </v-textarea>
                                </v-col>
                            </v-row>
                            <PatientDataBioInfo/>
                            <PatientDataContactInfo :requireFields="true" />
                        </v-form>
                    </ValidationObserver>
                </v-card-text>
            </v-card>
        </v-dialog>
        <TestProtocolListDialog :dialog="testProtocolListDialog" @dialog-close="testProtocolListDialogClose"/>
        <ConfirmationDialog v-if="confirmResendTestRequest" :params="confirmResendTestRequestParams" @confirmation-dialog-response="confirmResendTestRequestHandler"/>
        <ConfirmationDialog v-if="confirmLoseChanges" :params="confirmLoseChangesParams" @confirmation-dialog-response="confirmLoseChangesHandler"/>
        <ConfirmationDialog v-if="confirmDeleteTestRequest" :params="confirmDeleteTestRequestParams" @confirmation-dialog-response="confirmDeleteTestRequestHandler"/>
        <ConfirmationDialog v-if="confirmAddTestRequest" :params="confirmAddTestRequestParams" @confirmation-dialog-response="confirmAddTestRequestHandler"/>
        <ConfirmationDialog v-if="confirmUpdateTestRequest" :params="confirmUpdateTestRequestParams" @confirmation-dialog-response="confirmUpdateTestRequestHandler"/>
    </div>
</template>

<script>
    const clonedeep = require('lodash.clonedeep');
    import { v4 as uuidv4 } from 'uuid';
    import { mapState, mapActions, mapMutations } from 'vuex';
    import { ValidationObserver, ValidationProvider, extend } from 'vee-validate';
    import {required} from 'vee-validate/dist/rules';
    import PatientDataBioInfo from '@/components/patient-data-bio-info';
    import PatientDataContactInfo from '@/components/patient-data-contact-info';
    import TestProtocolListDialog from '@/components/test-protocol-list-dialog';
    import ConfirmationDialog from '@/components/confirmation-dialog';

    extend('requiredEyeChoice',
    {
        ...required,
        message: 'No eye choice specified.'
    });
    extend('requiredTestProtocol',
    {
        ...required,
        message: 'No test protocol specified.'
    });

    export default
    {
        name: 'test-request-dialog',
        components:
        {
            ValidationObserver,
            ValidationProvider,
            PatientDataBioInfo,
            PatientDataContactInfo,
            TestProtocolListDialog,
            ConfirmationDialog
        },
        data: function ()
        {
            return {
                dialogLocal: false,
                oldTestRequest: {},
                oldPatientData: {},
                newTestLocal: false,
                confirmResendTestRequest: false,
                confirmResendTestRequestParams:
                {
                    title: 'Resend Test Request',
                    text: '',
                    positiveResponse: 'Yes',
                    negativeResponse: 'No',
                    context: ''
                },
                confirmLoseChanges: false,
                confirmLoseChangesParams:
                {
                    title: 'About to Lose Changes',
                    text: 'You have unsaved test request updates. Lose changes?',
                    positiveResponse: 'Yes',
                    negativeResponse: 'No'
                },
                confirmDeleteTestRequest: false,
                confirmDeleteTestRequestParams:
                {
                    title: 'Delete Test Request',
                    text: 'Are you sure you want to delete this test request?',
                    positiveResponse: 'Yes',
                    negativeResponse: 'No'
                },
                confirmAddTestRequest: false,
                confirmAddTestRequestParams:
                {
                    title: 'Add Test Request',
                    text: 'The test request has been added.',
                    positiveResponse: 'OK'
                },
                confirmUpdateTestRequest: false,
                confirmUpdateTestRequestParams:
                {
                    title: 'Edit Test Request',
                    text: 'The test request has been saved.',
                    positiveResponse: 'OK'
                },
                testProtocolListDialog: false,
                testToken: '',
                eyeChoice: null,
                eyeChoices:
                [
                    'Both',
                    'Right',
                    'Left'
                ],
                localCreateForAllGroupMembers: false,
                baseline: false,
                testTag: '',
                scheduledDate: null,
                scheduledDateFormatted: '',
                scheduledDateMenu: false,
                scheduledTime: null,
                scheduledDateTime: null,
                postAction: null,
                tokenExpireDate: null,
                emailSubject: ''
            }
        },
        props:
        {
            dialog: Boolean(false),
            newTest: Boolean(false)
        },
        computed:
        {
            ...mapState('auth',
            {
                currentUser: state => state.currentUser,
                testProtocolSuite: state => state.testProtocolSuite
            }),
            ...mapState('tests',
            {
                patientData: state => state.patientData,
                patientDataToMutate: state => state.patientDataToMutate
            }),
            ...mapState('telehealth',
            {
                testRequestToMutate: state => state.testRequestToMutate,
                createForAllGroupMembers: state => state.createForAllGroupMembers
            }),
            ...mapState('common',
            {
                waiting: state => state.waiting
            })
        },
        watch:
        {
            async dialogLocal(val)
            {
                // If the dialog is being closed
                if (!val)
                {
                    this.HIDE_SNACKBAR();
                    this.SET_CREATE_FOR_ALL_GROUP_MEMBERS(this.localCreateForAllGroupMembers);
                    this.$emit('dialog-close', this.postAction);
                    this.eyeChoice = '';
                    this.testTag = '';
                    this.$refs.observer.reset()
                }
                else // Else the dialog is being opened
                {
                    this.$logEvent(this.$firebaseAnalytics, 'screen_view',
                    {
                        app_name: 'Web Portal',
                        screen_name: `Reflex Web Portal - ${this.$options.name}`,
                        app_version: process.env.VUE_APP_VERSION
                    });
                    this.dialogLocal = val;
                    this.localCreateForAllGroupMembers = this.createForAllGroupMembers;
                    this.newTestLocal = this.newTest;
                    this.eyeChoiceValueCollapsed();
                    this.testToken = this.newTestLocal ? uuidv4() : this.testRequestToMutate.testToken;
                    this.baseline = this.testRequestToMutate.baseline;
                    // If this test request does not have a test tag yet, load the list of them now to see if we can auto-assign one (for single tag cases)
                    if (!this.testRequestToMutate.testTag)
                    {
                        await this.loadTestProtocols()
                    }
                    else
                    {
                        this.testTag = this.testRequestToMutate.testTag
                    }
                    this.scheduledDate = this.formatDateForCalendar(new Date(this.testRequestToMutate.tokenExpireDate));
                    this.scheduledTime = this.formatDateForTime(new Date(this.testRequestToMutate.tokenExpireDate));
                    this.scheduledDateTime = new Date(this.testRequestToMutate.tokenExpireDate);
                    this.emailSubject = this.testRequestToMutate.emailSubject;
                    // You should call it on the next frame so the watches can update
                    window.requestAnimationFrame(async () =>
                    {
                        // Track the original data
                        this.oldPatientData = clonedeep(this.patientData);
                        this.oldTestRequest = clonedeep(this.testRequestToMutate)
                    })
                }
            },
            dialog(val)
            {
                this.dialogLocal = val
            },
            testToken(val)
            {
                this.testRequestToMutate.testToken = val
            },
            eyeChoice(val)
            {
                this.eyeChoiceValueExpanded(val)
            },
            baseline(val)
            {
                this.testRequestToMutate.baseline = val;
                this.testRequestToMutate.baselineDisplay = val ? 'Yes' : 'No'
            },
            testTag(val)
            {
                this.testRequestToMutate.testSuite = this.currentUser.testProtoSuite ? this.currentUser.testProtoSuite : null;
                this.testRequestToMutate.testTag = val
            },
            scheduledDate(val)
            {
                this.scheduledDateFormatted = this.formatDate(val);
                this.scheduledDateTime = new Date(`${val}T${this.formatDateForTime(this.scheduledDateTime)}`);
                this.testRequestToMutate.tokenExpireDate = this.scheduledDateTime.toISOString()
            },
            scheduledTime(val)
            {
                if (!val)
                {
                    return
                }
                this.scheduledDateTime = new Date(`${this.formatDateForCalendar(this.scheduledDateTime)}T${val}`);
                this.testRequestToMutate.tokenExpireDate = this.scheduledDateTime.toISOString()
            },
            emailSubject(val)
            {
                this.testRequestToMutate.emailSubject = val
            }
        },
        methods:
        {
            ...mapActions('auth', ['GET_TEST_PROTOCOL_SUITE']),
            ...mapActions('tests', ['SAVE_PATIENT_DATA']),
            ...mapActions('telehealth', ['SEND_TEST_REQUEST', 'DELETE_TEST_REQUEST']),
            ...mapMutations('telehealth', ['UPDATE_MUTATED_TEST_REQUEST', 'SET_CREATE_FOR_ALL_GROUP_MEMBERS']),
            ...mapMutations('common', ['WAITING', 'SHOW_SNACKBAR_WARN_DELAY', 'SHOW_SNACKBAR_ERROR', 'HIDE_SNACKBAR']),
            closeDialog()
            {
                if (this.newTestLocal || JSON.stringify(this.oldTestRequest) !== JSON.stringify(this.testRequestToMutate))
                {
                    this.confirmLoseChanges = true;
                    return
                }
                this.postClose(null)
            },
            postClose(postAction)
            {
                this.postAction = postAction;
                document.getElementById('patientTestRequestId').scrollTop = 0;
                this.dialogLocal = false
            },
            confirmLoseChangesHandler(payload)
            {
                this.confirmLoseChanges = false;
                // If the changes should be lost
                if (payload.response)
                {
                    this.postClose(null)
                }
            },
            async checkBeforeSend()
            {
                // If there is something to send and the validation has passed
                if (await this.$refs.observer.validate() && (this.newTestLocal || JSON.stringify(this.oldTestRequest) !== JSON.stringify(this.testRequestToMutate)))
                {
                    if (await this.send())
                    {
                        this.confirmUpdateTestRequest = true
                    }
                }
                // If the user may just want to resend the test request
                else if (await this.$refs.observer.validate())
                {
                    this.confirmResendTestRequestParams.text = `Are you sure you want to resend Test Request: ${this.testRequestToMutate.mrn}?`;
                    this.confirmResendTestRequestParams.context = this.testRequestToMutate;
                    this.confirmResendTestRequest = true
                }
            },
            async confirmResendTestRequestHandler(payload)
            {
                this.confirmResendTestRequest = false;
                // If the Test Request should be resent
                if (payload.response)
                {
                    if (await this.send())
                    {
                        this.confirmUpdateTestRequest = true
                    }
                }
            },
            async confirmUpdateTestRequestHandler()
            {
                this.confirmUpdateTestRequest = false;
                this.postClose('update')
            },
            async send()
            {
                try
                {
                    this.HIDE_SNACKBAR();
                    this.WAITING(true);
                    // If the email address or phone number has changed, update the patient record
                    if (JSON.stringify(this.oldPatientData) !== JSON.stringify(this.patientDataToMutate))
                    {
                        await this.SAVE_PATIENT_DATA({data: this.patientDataToMutate, oldMrn: this.patientDataToMutate.mrn})
                    }
                    await this.SEND_TEST_REQUEST({data: this.testRequestToMutate, groupId: this.patientDataToMutate.groupId, createForAllGroupMembers: this.localCreateForAllGroupMembers});
                    return true
                }
                catch (error)
                {
                    // If the patient record associated with the test request was not found
                    if (404 === error.response.status)
                    {
                        this.SHOW_SNACKBAR_WARN_DELAY({message: 'The associated patient ID could not be found.', delay: 10000})
                    }
                    else if (403 === error.response.status)
                    {
                        if (error.response.data.reflexError)
                        {
                            const code = error.response.data.reflexError.code;
                            if (1 === code)
                            {
                                this.SHOW_SNACKBAR_WARN_DELAY({message: 'You are not currently subscribed to Reflex or in a free trial.', delay: 10000})
                            }
                            else if (2 === code)
                            {
                                this.SHOW_SNACKBAR_WARN_DELAY({message: 'The organization you belong to is not currently activated to use Reflex.', delay: 10000})
                            }
                            else if (3 === code)
                            {
                                this.SHOW_SNACKBAR_WARN_DELAY({message: 'Your Reflex subscription type is not at the Pro level which is required for Telehealth.', delay: 10000})
                            }
                            else if (4 === code)
                            {
                                this.SHOW_SNACKBAR_WARN_DELAY({message: 'Test request has expired or has already been used. Please create a new test request.', delay: 10000})
                            }
                            else
                            {
                                this.SHOW_SNACKBAR_WARN_DELAY({message: error.message, delay: 10000})
                            }
                        }
                    }
                    else
                    {
                        this.SHOW_SNACKBAR_ERROR(error.message)
                    }
                    return false
                }
                finally
                {
                    this.WAITING(false)
                }
            },
            async confirmDeleteTestRequestHandler(payload)
            {
                this.confirmDeleteTestRequest = false;
                // If the test request should be deleted
                if (payload.response)
                {
                    await this.deleteTestRequestActual()
                }
            },
            deleteTestRequest()
            {
                this.confirmDeleteTestRequest = true
            },
            async deleteTestRequestActual()
            {
                try
                {
                    this.HIDE_SNACKBAR();
                    this.WAITING(true);
                    await this.DELETE_TEST_REQUEST({testToken: this.testToken});
                    this.postClose('delete')
                }
                catch (error)
                {
                    this.SHOW_SNACKBAR_WARN_DELAY({message: error.message, delay: 10000})
                }
                this.WAITING(false)
            },
            async confirmAddTestRequestHandler()
            {
                this.confirmAddTestRequest = false;
            },
            async loadTestProtocols()
            {
                try
                {
                    this.WAITING(true);
                    await this.GET_TEST_PROTOCOL_SUITE()
                    // If there is just one protocol tag, automatically select it
                    if (1 === this.testProtocolSuite.testProtocols.length)
                    {
                        this.testTag = this.testProtocolSuite.testProtocols[0].tag
                    }
                }
                catch{}
                this.WAITING(false)
            },
            testProtocolListDialogClose(payload)
            {
                this.testProtocolListDialog = false;
                if (payload)
                {
                    this.testTag = payload.tag
                }
            },
            eyeChoiceValueCollapsed()
            {
                if (true === this.testRequestToMutate.rightEye && true === this.testRequestToMutate.leftEye)
                {
                    this.eyeChoice = 'Both'
                }
                else if (true === this.testRequestToMutate.rightEye && false === this.testRequestToMutate.leftEye)
                {
                    this.eyeChoice = 'Right'
                }
                else if (false === this.testRequestToMutate.rightEye && true === this.testRequestToMutate.leftEye)
                {
                    this.eyeChoice = 'Left'
                }
                else // Default to both eyes if none are selected
                {
                    this.eyeChoice = 'Both'
                }
            },
            eyeChoiceValueExpanded(eyeChoice)
            {
                if ('Both' === eyeChoice)
                {
                    this.testRequestToMutate.rightEye = true;
                    this.testRequestToMutate.rightEyeDisplay = 'Yes';
                    this.testRequestToMutate.leftEye = true;
                    this.testRequestToMutate.leftEyeDisplay = 'Yes'
                }
                else if ('Right' === eyeChoice)
                {
                    this.testRequestToMutate.rightEye = true;
                    this.testRequestToMutate.rightEyeDisplay = 'Yes';
                    this.testRequestToMutate.leftEye = false;
                    this.testRequestToMutate.leftEyeDisplay = 'No';
                }
                else
                {
                    this.testRequestToMutate.rightEye = false;
                    this.testRequestToMutate.rightEyeDisplay = 'No';
                    this.testRequestToMutate.leftEye = true;
                    this.testRequestToMutate.leftEyeDisplay = 'Yes'
                }
                this.UPDATE_MUTATED_TEST_REQUEST(this.testRequestToMutate)
            },
            formatDate(date)
            {
                if (!date)
                {
                    return null
                }
                return new Date(date).toLocaleDateString(navigator.languages[0], {timeZone: 'UTC'})
            },
            formatDateForCalendar(date)
            {
                return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`
            },
            formatDateForTime(date)
            {
                return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
            }
        }
    };
</script>

<style scoped>
</style>